/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  /**
   * Adds the Section Rendering API parameter to a URL (before any #fragment).
   * @param {string} href - URL.
   * @param {string} sectionId - Id of the section to render.
   * @returns {string}
   */
  const addSectionId = (href, sectionId) => {
    const hashAt = href.indexOf('#');
    const base = hashAt === -1 ? href : href.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : href.slice(hashAt);
    return `${base}${base.indexOf('?') === -1 ? '?' : '&'}section_id=${encodeURIComponent(sectionId)}${hash}`;
  };

  /**
   * Removes the Section Rendering API parameter from a URL, so that a link taken from a section answer is a normal page link.
   * @param {string} href - URL.
   * @returns {string}
   */
  const stripSectionId = (href) => href
    .replace(/([?&])section_id=[^&#]*(&?)/, (match, lead, tail) => (tail ? lead : ''))
    .replace(/[?&](#|$)/, '$1');

  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      // The block that holds the pagination. It is made invisible (its space is kept) from the start of a page fetch until the
      // new rows have been painted, so that Chrome does not score it as a layout shift when the rows push it away.
      this.navBox = this.pagination ? this.pagination.closest('nav') || this.pagination : null;

      // What sits below the list (the footer, sections after it). A list that has data-hold-below hides it, like the pagination block,
      // while it is on screen at the moment new rows go in (see holdBelow). Without the attribute nothing changes.
      this.holdBelowOn = this.hasAttribute('data-hold-below');
      this.heldBelow = [];

      // Infinite scroll loads one page per scroll gesture. `armed`: the next sight of the pagination inside the observed zone may
      // load a page. It is set again when the pagination leaves the zone (the new rows usually push it out) or when the visitor
      // has scrolled 64 px or more since the rows were inserted (a jump to the bottom keeps the pagination in view).
      this.armed = true;
      this.inZone = false;
      this.scrollBase = null;

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          // Start loading while the pagination is still up to 1500 px below the screen, so the new rows normally go in out of sight.
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.inZone = entry.isIntersecting;
                if (!entry.isIntersecting) {
                  this.armed = true;
                  return;
                }
                if (this.armed) this.loadMore();
              });
            },
            { rootMargin: '0px 0px 1500px 0px' }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.controller) this.controller.abort();
      if (this.paginationObserver) this.paginationObserver.disconnect();
      this.unwatchScroll();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
      // A restore from the back/forward cache (reload) must never leave the pagination hidden or a fetch marked as running.
      this.showNav();
      this.isFetching = false;
    }

    /**
     * Hides the pagination block. It keeps its space, nothing else on the page moves.
     */
    hideNav() {
      if (this.navBox) this.navBox.style.visibility = 'hidden';
    }

    /**
     * Shows the pagination block again.
     */
    showNav() {
      if (this.navBox) this.navBox.style.removeProperty('visibility');
      this.releaseBelow();
    }

    /**
     * Hides what is below the list, if it is on screen, from the moment new rows are inserted until one frame after they have been
     * painted (showNav). A visitor who jumped to the bottom, or who pressed "Show more", has the footer in view; the new rows push it
     * down and Chrome scores that as a layout shift (0.47 to 1.2 on the collection list). A hidden element keeps its space and is not
     * scored; the rows themselves appear exactly where they always did. An element that holds the keyboard focus is left alone.
     */
    holdBelow() {
      this.releaseBelow();
      const section = this.closest('.shopify-section');
      const below = [];
      for (let el = section && section.nextElementSibling; el; el = el.nextElementSibling) below.push(el);
      const footer = document.querySelector('.shopify-section-group-footer-group');
      if (footer && below.indexOf(footer) === -1 && !footer.contains(this)) below.push(footer);
      below.forEach((el) => {
        if (el.style.visibility || el.contains(document.activeElement)) return;
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          el.style.visibility = 'hidden';
          this.heldBelow.push(el);
        }
      });
    }

    /**
     * Shows again what holdBelow hid.
     */
    releaseBelow() {
      if (!this.heldBelow) return;
      this.heldBelow.forEach((el) => el.style.removeProperty('visibility'));
      this.heldBelow = [];
    }

    /**
     * Listens for a new scroll after a page was loaded (see `armed`).
     */
    watchScroll() {
      this.scrollHandler = this.scrollHandler || this.handleScroll.bind(this);
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    unwatchScroll() {
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    }

    /**
     * A scroll of 64 px or more after the last insert allows the next page to load, if the pagination is in the observed zone.
     */
    handleScroll() {
      if (this.armed || this.isFetching || this.scrollBase === null) return;
      if (Math.abs(window.scrollY - this.scrollBase) < 64) return;
      this.armed = true;
      this.unwatchScroll();
      if (this.inZone) this.loadMore();
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Fetches and parses a URL.
     * @param {string} url - URL to fetch.
     * @returns {Promise<HTMLTemplateElement>} The parsed answer.
     */
    async fetchTemplate(url) {
      const response = await fetch(url, {
        signal: this.controller.signal
      });

      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      return tmpl;
    }

    /**
     * Fetches the next page of results. Only the section this element is in is requested (Section Rendering API, a fraction
     * of the page). On an error, or an answer without results and pagination, the whole page is fetched as before.
     * @param {string} href - URL of the next page.
     * @returns {Promise<HTMLTemplateElement>} The parsed answer.
     */
    async fetchNextPage(href) {
      const section = this.closest('.shopify-section');
      const prefix = 'shopify-section-';
      const sectionId = section && section.id.indexOf(prefix) === 0 ? section.id.slice(prefix.length) : '';
      const pageUrl = stripSectionId(href);

      if (sectionId) {
        try {
          const tmpl = await this.fetchTemplate(addSectionId(pageUrl, sectionId));
          if (tmpl.content.querySelector('.js-pagination-result') && tmpl.content.querySelector('.js-pagination')) return tmpl;
        } catch (error) {
          // A navigation away aborts the request: nothing more to fetch.
          if (error.name === 'AbortError') throw error;
        }
      }

      return this.fetchTemplate(pageUrl);
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.armed = false;
        this.scrollBase = null;
        this.watchScroll();
        const hadFocus = !!(this.navBox && this.navBox.contains(document.activeElement));
        const firstNewIndex = this.results.length;
        let showScheduled = false;
        // B6F L2: the pagination stays visible while the beam runs and is hidden only for the insert below.
        if (!this.hasAttribute('data-ub6f-more')) this.hideNav();
        this.controller = new AbortController();

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve and parse the next page (only the enclosing section of it, see fetchNextPage)
          const tmpl = await this.fetchNextPage(this.loadMoreButton.href);

          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          // What is below the list and on screen stays out of sight while the rows go in (see holdBelow)
          if (this.holdBelowOn) this.holdBelow();

          // Append the additional results
          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          this.hideNav();
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');
          this.dispatchEvent(new CustomEvent('on:pagination:loaded', { bubbles: true }));

          // TR11: with the "Show more" button (not infinite scroll) the address follows the depth, so a reload or a return lands on ?page=N.
          // B6F L2 (data-ub6f-more) keeps the address and restores every loaded page instead (assets/umbra-b6f.js).
          if (!this.infiniteScroll && !this.hasAttribute('data-ub6f-more')) {
            try {
              const nextUrl = new URL(this.loadMoreButton.href, window.location.href);
              nextUrl.searchParams.delete('section_id');
              nextUrl.hash = '';
              if (nextUrl.origin === window.location.origin) window.history.replaceState(window.history.state, '', nextUrl.pathname + nextUrl.search);
            } catch (error) {
              // A URL that cannot be parsed leaves the address as it is.
            }
          }

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination.dataset.isMoreResults) {
            // The links of a section answer carry section_id: make them normal page links again.
            newPagination.querySelectorAll('a[href*="section_id="]').forEach((link) => {
              link.setAttribute('href', stripSectionId(link.getAttribute('href')));
            });
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
            }
          } else {
            this.pagination.remove();
            if (this.paginationObserver) this.paginationObserver.disconnect();
            // That was the last page: nothing may load again
            this.unwatchScroll();
            this.inZone = false;
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }

          // Show the pagination again one frame after the frame that first paints the new rows, and remember where the visitor
          // is. A keyboard user who pressed "Show more" (focus was on it) lands on the first new product, not on the page body.
          showScheduled = true;
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              this.showNav();
              this.scrollBase = window.scrollY;
              if (hadFocus) {
                const first = this.results[firstNewIndex];
                const target = first && first.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"])');
                if (target) target.focus({ preventScroll: true });
              }
            })
          );
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }

          // A failed or aborted fetch: show the pagination at once; the next scroll gesture tries again (no retry loop).
          if (!showScheduled) {
            this.showNav();
            this.scrollBase = window.scrollY;
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
